<template>
  <div class="document-container">
    <es-run-dc-extended-modal
      v-if="showConfirmation"
      @closeModal="cancelRun"
      @confirm="runNow"/>
    <div class="document">
      <div class="header">
        <EsHeader>
          <template #header>
            <div class="header-title">Match Options</div>
          </template>
        </EsHeader>
      </div>
      <div class="tabs">
        <ul>
          <li v-if="hasModule('DRMCH:MATCH:OPTIONS:PERSONTAB:VIEW')" @click="$router.push({ name: 'personoptions' })">
            <a>PERSON</a>
          </li>
          <li class="is-active">
            <a>ADDRESS</a>
          </li>
        </ul>
      </div>
    </div>
    <ul class="summary-panels wrap is-flex">
      <li class="document flex-item flex-item-header grow testing">
        <div class="panel-container is-flex">
          <div class="is-size-4 has-text-weight-bold is-flex">
            CEP DC Extended Match
          </div>
          <div class="panel-subtitle has-text-grey is-flex">
            View and match household addresses for DC Extended.
          </div>
        </div>
      </li>
      <li class="document flex-item">
        <div class="panel-container is-flex">
          <div v-if="header.startDate" class="is-size-4 has-text-weight-bold">
            {{ header.endDate || header.startDate | formatDateMMDDYYYY('--') }}
          </div>
          <div v-else class="is-size-4 has-text-weight-bold">
            --
          </div>
          <div class="panel-subtitle has-text-grey">
            Last Run Date
          </div>
        </div>
      </li>
      <li class="document flex-item">
        <div class="panel-container is-flex">
          <div v-if="header.status" class="is-size-4 has-text-weight-bold"
           :title="header.status === 'PROCESSING'
            ? header.hoursLeftEstimate && header.hoursLeftEstimate + ' hours remaining' : ''">
            <font-awesome-icon
            v-if="header.status === 'PROCESSING'"
            :icon="['fad', 'spinner']" spin/>
            {{ header.status === 'PROCESSING' ? 'In Progress' : header.status }}
          </div>
          <div v-else class="is-size-4 has-text-weight-bold">
            --
          </div>
          <div class="panel-subtitle has-text-grey">
            Status
          </div>
        </div>
      </li>
      <li class="document flex-item">
        <div class="panel-container is-flex">
          <div v-if="header.startDate"  class="is-size-4 has-text-weight-bold">
            {{ matchesQueued }}
          </div>
          <div v-else class="is-size-4 has-text-weight-bold">
            --
          </div>
          <div class="panel-subtitle has-text-grey">
            Matches Queued
          </div>
        </div>
      </li>
    </ul>
    <div class="columns is-vcentered card-container">
      <div class="column has-text-left has-text-grey">Match Results</div>
      <div v-if="header.status !== 'PROCESSING'">
        <div v-if="hasModule('DRMCH:MATCH:OPTIONS:ADDRESS:RUN')" class="column has-text-right">
          <button class="button is-primary"
            :disabled = "header.status === 'PROCESSING'"
            @click="askConfirm">
            Run Now
          </button>
        </div>
      </div>
      <div v-else>
        <button class="button is-primary"
            @click="refresh">
            Refresh
          </button>
      </div>
    </div>
    <es-loader v-if="loading === true"/>
    <div v-else class="result-container">
      <div class="refresh-container"
        v-if="!matchResults || this.matchesQueued === 0 || header.status === 'ERROR'">
        <div class="icon">
          <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="3x"/>
        </div>
        <div v-if="header.status === 'ERROR'" class="is-size-5">
          There was an issue. Please try again or contact your administrator.
        </div>
        <div v-else class="is-size-5">
          No results were found.
        </div>
      </div>
      <dc-extended-match v-else v-for="(match, i) in matchResults" :key="`${i}-${match.id}`"
        :match="match" @refresh="refresh"/>
        <div>
          <a v-if="this.pageSize < this.matchesQueued" @click="loadMore()">Load More</a>
        </div>
    </div>
    <dc-extended-legend class="legend"
      :lower="getProperty('ELASTIC_LOCATION-LOWER_BOUND_FOR_NEAR_MATCHES')"
      :upper="getProperty('ELASTIC_LOCATION-UPPER_BOUND_FOR_NEAR_MATCHES')"
      v-if="loading === false"/>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import DcExtendedMatch from '@/components/DcExtendedMatch.vue';
import EsHeader from '@/components/EsHeader.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import EsLoader from '@/components/EsLoader.vue';
import DcExtendedLegend from '@/components/DcExtendedLegend.vue';
import NotificationManager from '@/mixins/NotificationManager';
import EsRunDcExtendedModal from '@/components/EsRunDcExtendedModal.vue';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faSpinner,
  faExclamationCircle,
);

export default {
  mixins: [BreadcrumbsManager, NotificationManager, PermissionManager],
  components: {
    DcExtendedMatch,
    EsHeader,
    EsLoader,
    DcExtendedLegend,
    EsRunDcExtendedModal,
  },
  data() {
    return {
      header: {},
      matchResults: [],
      loading: true,
      pageSize: 25,
      matchesQueued: 0,
      showConfirmation: false,
      pending: [],
    };
  },
  watch: {
    pageSize(val) {
      if (val > 1) {
        this.refresh();
      }
    },
  },
  created() {
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'directMatch' } },
      { text: 'Match Options', to: {} },
    ]);
  },
  mounted() {
    this.onLoad();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'getProperty',
    ]),
    ...mapGetters('lookups', [
      'activeGenders',
      'activeGrades',
    ]),
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadGrades',
    ]),
    async onLoad() {
      this.loading = true;

      try {
        await Promise.all([
          this.loadGenders(),
          this.loadGrades(),
          this.getMatchHeader(),
          this.getMatches(),
        ]);
        const unsorted = this.groupResults(this.pending);
        this.matchResults = unsorted.sort((a, b) => b.percent - a.percent);
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loading = false;
      }
    },
    getMatchHeader() {
      return axios.get('process-control?type=DC_EXTENDED_MATCHING')
        .then((res) => {
          this.header = res.data;
        }).catch(this.handleError);
    },
    async runNow() {
      this.toggleConfirmation();
      try {
        const res = await axios.post('/directmatch/address', {});
        if (res.status === 200) this.showNotification('success', ['Matching is in progress']);
        this.getMatchHeader();
      } catch (error) {
        this.handleError(error);
      }
    },
    handleError(err) {
      const { status } = err.response;
      let apiMsg = '';

      if (status === 500) {
        apiMsg = 'Network Error';
      } else if (status === 400) {
        apiMsg = `${err.response.data.message}`;
      } else {
        apiMsg = 'Something went wrong.';
      }
      this.showNotification('danger', [apiMsg]);
    },
    getMatches() {
      this.reset();
      return axios.get('/directmatch/address/pending', {
        params: {
          pageSize: this.pageSize,
          open: true,
        },
      }).then((res) => {
        this.pending = res.data.data;
        this.matchesQueued = res.data.totalCount;
      }).catch(this.handleError);
    },
    groupResults(resData) {
      const unsorted = [];
      if (resData && resData.length > 0) {
        for (let j = 0; j < resData.length; j += 1) {
          this.addMatchedData(resData[j], unsorted);
        }
      }
      return unsorted;
    },
    addMatchedData(matchResult, matchResults) {
      const matchTrans = matchResult.dcxMatchTransIds;
      const addresses = [...new Set(matchTrans
        .map(t => t.student.street1 + t.student.street2 + t.student.city + t.student.stateCode + t.student.zip))];
      for (let i = 0; i < addresses.length; i += 1) {
        const m = {
          id: matchResult.id,
          matchTypeId: matchResult.matchTypeId,
          eligable: matchResult.targetStudent,
          matched: null,
          percent: 0,
          marker: 'nomatch',
          studentsEligible: 0,
          students: [],
          districtName: matchResult.districtName,
          schoolName: matchResult.schoolName,
          identifiedDate: matchResult.identifiedDate,
        };
        for (let j = 0; j < matchTrans.length; j += 1) {
          const s = matchTrans[j].student;
          if (s.street1 + s.street2 + s.city + s.stateCode + s.zip === addresses[i]) {
            if (!m.matched) {
              m.matched = this.getaddress(matchTrans[j].student);
              m.percent = matchTrans[j].matchPercent.toFixed(2) * 100;
              m.marker = this.getMarker(m.percent);
            }
            s.districtName = matchTrans[j].districtName;
            s.schoolName = matchTrans[j].schoolName;
            m.students.push(this.getStudent(s));
          }
        }
        m.studentsEligible = m.students.length;
        matchResults.push(m);
      }
    },
    getaddress(student) {
      return {
        street1: student.street1,
        street2: student.street2,
        city: student.city,
        stateCode: student.stateCode,
        zip: student.zip,
      };
    },
    getStudent(student) {
      const grade = this.activeGrades.find(g => g.code === student.gradeCode);
      const gender = this.activeGenders.find(g => g.code === student.genderCode);
      return {
        stateStudentId: student.stateStudentId,
        name: `${student.firstName} ${student.lastName}`,
        grade: grade !== undefined ? grade.description : student.gradeCode,
        gender: gender !== undefined ? gender.description : student.genderCode,
        dob: student.dateOfBirth,
        district: {
          code: student.districtCode,
          name: student.districtName,
        },
        school: {
          code: student.schoolCode,
          name: student.schoolName,
        },
      };
    },
    getMarker(percent) {
      if (percent >= this.getProperty('ELASTIC_LOCATION-UPPER_BOUND_FOR_NEAR_MATCHES')) {
        return 'match';
      }
      return 'nearmatch';
    },
    reset() {
      this.matchResults = [];
    },
    loadMore() {
      if (this.pageSize < this.matchesQueued) {
        this.pageSize += this.pageSize;
      }
    },
    askConfirm() {
      this.toggleConfirmation();
    },
    toggleConfirmation() {
      this.showConfirmation = !this.showConfirmation;
    },
    cancelRun() {
      this.toggleConfirmation();
    },
    refresh() {
      this.loading = true;
      Promise.all([
        this.getMatchHeader(),
        this.getMatches(),
      ]).then(() => {
        const unsorted = this.groupResults(this.pending);
        this.matchResults = unsorted.sort((a, b) => b.percent - a.percent);
      }).catch(this.handleError)
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="sass" scoped>
  .tabs
    padding: 1rem 1rem 0 1rem
    a
      &:hover
        text-decoration: none
  .wrap
    -webkit-flex-wrap: wrap
    flex-wrap: wrap
  .flex-item
    min-width: 206px
    min-height: 80px
    margin: 0rem 1rem 1rem 0rem
    text-align: center
  .flex-item-header
    min-width: 420px
  .grow
    flex-grow: 1
  .summary-panels
    padding-top: 1rem
    margin-right: -1rem
  .panel-container
    padding: 1rem
    height: 100%
    flex-direction: column
    justify-content: center
  .panel-container--isp
    padding: 1rem
    height: 100%
    width: 100%
    justify-content: center
  .isp-box
    flex-direction: column
    padding-left: 1rem
  .panel-subtitle
    font-size: 12px
  .header-panel
    flex-grow: 1
    margin-right: 1.0rem
    min-width: 420px
  .refresh-container
    padding: 6.5rem
  .icon
    margin-bottom: 2.5rem
  .legend
    margin-top: 1rem
  .card-container
    margin-left: auto
    margin-right: auto
</style>
